import {
    ref, computed, watch, Ref
} from 'vue';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { useBacklogConfigurationsStore } from '@/stores/backlogConfigurationsStore';
import {
    ApiRespBacklogAgeBandsBucketsByWorkstreams,
    BacklogAgeBandBucketsByWorkstream
} from '@/pages/OperationsInsights/types/BacklogMetricsResponse';

import { fetchBacklogAgeBandsBucketsByWorkstreams } from '@/pages/OperationsInsights/services/fetchBacklogAgeBandsBucketsByWorkstreams';
import { useWorkstreamDefinitionsStore } from '@agyletime/vue-3-common/src/composables/workstreamDefinitionsStore';
import OpenBacklogAgeBandByWsCellRenderer from '@/pages/agGridUtils/OpenBacklogAgeBandByWsCellRenderer.vue';
const { workstreamDefinitionsMap } = useWorkstreamDefinitionsStore();
const { ageBandConfigNamesStore } = useBacklogConfigurationsStore();

export function useBLAgeBandsWorkstreamsData(queryParams: Ref<ApiQueryParams>) {
    const logger = useLogger('useBLAgeBandsBucketsData');
    const ageBandsDataByWorkstream = ref<ApiRespBacklogAgeBandsBucketsByWorkstreams | null>(null);

    const isLoading = ref(false);
    const error = ref<string | null>(null);

    const AGE_BAND_EOP_SUFFIX = '__NetChange';
    const generateAgeBandLastIntervalColName = (ageBandName: string) => `${ageBandName}${AGE_BAND_EOP_SUFFIX}`;

    // Grid Data - Helper function to transform Backlog AgeBand Buckets ByWorkstream to grid data
    const transformBacklogAgeBandBucketByWsToGridData = (metrics: BacklogAgeBandBucketsByWorkstream[]) => {
        const gridMap = new Map<number, object>();
        for (const wsMetrics of metrics) {
            const name = workstreamDefinitionsMap.get(wsMetrics.workstreamDefinitionId)?.name ?? `Unknown (${wsMetrics.workstreamDefinitionId})`;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const row: any = { name };

            for (const ageBandBucketsByAgeBandName of wsMetrics.ageBandsBuckets) {
                const { ageBandName } = ageBandBucketsByAgeBandName;
                row[ageBandName] = ageBandBucketsByAgeBandName.lastIntervalOpenCount;
                // Tracking to display __NetChange
                row[generateAgeBandLastIntervalColName(ageBandName)] = ageBandBucketsByAgeBandName.netChangesOpenCount;
            }
            gridMap.set(wsMetrics.workstreamDefinitionId, row);
        }
        return Array.from(gridMap.values());
    };

    const extractAgeBandNames = (metrics: BacklogAgeBandBucketsByWorkstream[]) => {
        const ageBandNames: string[] = [];
        for (const metric of metrics) {
            for (const band of metric.ageBandsBuckets) {
                if (!ageBandNames.includes(band.ageBandName)) {
                    ageBandNames.push(band.ageBandName);
                }
            }
        }
        return ageBandNames;
    };

    // Grid Data and Column Def for (backlog age by workstream)
    const backlogAgeByWorkstreamGridData = computed(() => {
        if (!ageBandsDataByWorkstream.value) return [];
        return transformBacklogAgeBandBucketByWsToGridData(ageBandsDataByWorkstream.value.metrics);
    });

    const backlogAgeByWorkstreamColumnDefs = computed(() => {
        const logger = useLogger('ageBandConfig-race-condition');
        logger.info('ageByWorkstream', {
            ageBandDataByWsVal: ageBandsDataByWorkstream.value,
            ageBandConfigNamesStoreData: ageBandConfigNamesStore.data
        });
        if (!ageBandsDataByWorkstream.value) return [];
        const ageBandNames = extractAgeBandNames(ageBandsDataByWorkstream.value.metrics);

        const finalSortedAgeBand: string[] = [];
        ageBandConfigNamesStore.data.forEach( (i) => finalSortedAgeBand.push(i));

        ageBandNames.filter((i) => !finalSortedAgeBand.includes(i)).forEach( (i) => finalSortedAgeBand.push(i));

        return [
            {
                field: 'name', headerName: 'Workstream', flex: 2, filter: true
            },
            ...finalSortedAgeBand.map( (ageBandName) => { return {
                field: ageBandName,
                headerName: ageBandName,
                dataType: 'number',
                flex: 1,
                filter: true,
                cellRenderer: OpenBacklogAgeBandByWsCellRenderer
            };})
        ];
    });

    // Fetch Data
    const fetchData = async () => {
        if (!queryParams.value) return;
        if (!queryParams.value.workstreamDefinitionIds?.length) return;

        isLoading.value = true;
        error.value = null;

        try {
            const [ageBandsByWorkstream] = await Promise.all([fetchBacklogAgeBandsBucketsByWorkstreams(queryParams.value)]);
            ageBandsDataByWorkstream.value = ageBandsByWorkstream;
        } catch (err) {
            error.value = 'Failed to fetch backlog age band data by workstreams';
            logger.error('Failed to fetch backlog age band data by workstreams', { error: err });
        } finally {
            isLoading.value = false;
        }
    };

    // Watch queryParams and fetch data when they change
    watch(queryParams, fetchData, { immediate: true, deep: true });
    return {
        backlogAgeByWorkstreamGridData,
        backlogAgeByWorkstreamColumnDefs,
        isLoading,
        error,
        fetchData
    };

}